<template>
  <div>
    <!-- ---------------------------------------------------------- hero -- -->
    <section class="hero" :style="{ backgroundImage: `url(${heroImage})` }">
      <div class="site_container">
        <p v-reveal="50" class="site_eyebrow">Znajdź auto marzeń</p>
        <h1 v-reveal="150">Import samochodów z Japonii.</h1>
        <p v-reveal="150" class="hero_subtitle">Import oparty na danych i weryfikacji technicznej</p>

        <p class="hero_actions">
          <router-link to="/aukcje/" class="site_button">Sprowadź dostepne aukcje <ArrowIcon /></router-link>
        </p>

        <p v-reveal="950" class="hero_lead">
          Sprowadzamy auta bezpośrednio z japońskich domów aukcyjnych – bez pośredników,
          z pełną obsługą od wyboru pojazdu aż po rejestrację w Polsce.
        </p>

        <a v-reveal:down="1350" class="site_hero_scroll" href="#dlaczego-my" aria-label="Przejdź niżej">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <polyline points="7 13 12 18 17 13" />
            <polyline points="7 6 12 11 17 6" />
          </svg>
        </a>
      </div>
    </section>

    <div class="brands">
      <SiteMarquee :items="brands" :duration="40" />
    </div>

    <!-- ------------------------------------------------------ why us -- -->
    <section id="dlaczego-my" class="site_section">
      <div class="site_container">
        <div class="why_header">
          <div>
            <p class="site_eyebrow">Dlaczego my</p>
            <h2>Czym Japonia Cars różni się od innych importerów?</h2>
          </div>
          <p>
            Przez ponad 10 lat i ponad 5 000 sprowadzonych aut dopracowaliśmy proces, który
            pozwala dokładnie oszacować koszt jeszcze przed złożeniem oferty na aukcji.
          </p>
        </div>

        <div class="why_tabs" role="tablist">
          <button
            v-for="(reason, i) in reasons"
            :key="reason.tab"
            :id="`why-tab-${i}`"
            class="why_tab"
            :class="{ is_active: i === reason_open }"
            type="button"
            role="tab"
            :aria-selected="i === reason_open"
            :aria-controls="`why-panel-${i}`"
            @click="reason_open = i"
          >
            {{ reason.tab }}
          </button>
        </div>

        <div
          v-for="(reason, i) in reasons"
          v-show="i === reason_open"
          :key="reason.tab"
          :id="`why-panel-${i}`"
          class="why_panel"
          role="tabpanel"
          :aria-labelledby="`why-tab-${i}`"
        >
          <div class="why_card">
            <p class="site_eyebrow site_eyebrow--accent">— {{ String(i + 1).padStart(2, '0') }} / {{ reason.tab }}</p>
            <h3>{{ reason.title }}</h3>
            <p>{{ reason.text }}</p>
          </div>
          <figure>
            <img :src="reason.image" :alt="reason.title" loading="lazy" />
          </figure>
        </div>
      </div>
    </section>

    <!-- ------------------------------------------------------- offer -- -->
    <section class="site_section site_section--paper">
      <div class="site_container">
        <div class="why_header">
          <div>
            <p class="site_eyebrow">Oferta</p>
            <h2>Co możemy dla Ciebie zrobić?</h2>
          </div>
          <p>
            Trzy ścieżki, jeden zespół. Opisz wymarzony model – znajdziemy go na japońskich
            aukcjach. Albo wybierz auto już dostępne w salonie lub konkretny pojazd
            z aktualnej licytacji.
          </p>
        </div>

        <!-- The first path spans the full width and carries a photo; the other
             two sit side by side underneath it. -->
        <ul class="paths">
          <li v-for="path in paths" :key="path.eyebrow" v-reveal :class="path.theme">
            <div>
              <p class="site_eyebrow site_eyebrow--accent">{{ path.eyebrow }}</p>
              <h3>{{ path.title }}</h3>
              <p>{{ path.text }}</p>
              <router-link v-if="path.link" :to="path.link.to" class="site_button">
                {{ path.link.label }} <ArrowIcon />
              </router-link>
            </div>
            <figure v-if="path.image">
              <img :src="path.image" :alt="path.title" loading="lazy" />
            </figure>
          </li>
        </ul>
      </div>
    </section>

    <!-- ------------------------------------------------------ about -- -->
    <section class="site_section about">
      <div class="site_container about_grid">
        <div>
          <p class="site_eyebrow site_eyebrow--accent">O nas</p>
          <h2>Kim jesteśmy?</h2>
          <p class="about_lead">
            Jesteśmy jedynym importerem w Polsce, który łączy 10-letnie doświadczenie
            logistyczne z własnym zapleczem serwisowym. Przez dekadę sprowadziliśmy ponad
            5 000 samochodów, zamieniając skomplikowany proces importu z Japonii w bezpieczną
            i przewidywalną usługę. Nie szukamy pośredników – posiadamy własny warsztat,
            w którym przygotowujemy auta technicznie, zajmujemy się ich rejestracją oraz
            pełną obsługą ubezpieczeniową. Wybierając Japonia Cars, stawiasz na skalę
            i profesjonalizm, gdzie każdy koszt jest znany jeszcze przed licytacją, a stan
            techniczny pojazdu zostaje potwierdzony przez naszych specjalistów.
          </p>

          <ul class="site_ticks">
            <li v-for="service in services" :key="service"><CheckIcon /> {{ service }}</li>
          </ul>
        </div>

        <div class="about_score">
          <SiteCounter :value="5000" suffix="+" />
          <p>sprowadzonych pojazdów dla klientów indywidualnych i firmowych z całej Polski.</p>
          <img src="/media/home-warsztat.webp" width="623" height="341" alt="Samochód w warsztacie Japonia Cars" loading="lazy" />
        </div>
      </div>
    </section>

    <!-- ------------------------------------------------------- stock -- -->
    <section class="site_section site_section--paper">
      <div class="site_container">
        <div class="why_header">
          <div>
            <p class="site_eyebrow">Aktualna oferta</p>
            <h2>Auta dostępne od zaraz.</h2>
            <p>Pojazdy sprowadzone, po inspekcji technicznej, z pełną dokumentacją.</p>
          </div>
          <router-link to="/samochody/" class="site_button site_button--outline">Zobacz wszystkie auta</router-link>
        </div>

        <ul class="stock_grid">
          <li v-for="car in featuredCars" :key="car.slug" v-reveal>
            <CarCard :car="car" />
          </li>
        </ul>
      </div>
    </section>

    <!-- ------------------------------------------------- testimonials -- -->
    <section class="site_section">
      <div class="site_container">
        <div class="why_header">
          <div>
            <p class="site_eyebrow">Opinie klientów</p>
            <h2>Co mówią o nas klienci?</h2>
          </div>
          <p>
            Cztery słowa na każdej opinii: rzetelność, transparentność, dotrzymane terminy.
            Wybór 4 z ponad 200 zweryfikowanych opinii.
          </p>
        </div>

        <!-- Two reviews at a time, scrolled with the arrows or by swiping. -->
        <div class="reviews">
          <button class="reviews_arrow" type="button" aria-label="Poprzednie opinie" @click="scrollReviews(-1)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" />
            </svg>
          </button>

          <ul ref="reviews" class="reviews_track">
            <li v-for="(review, i) in reviews" :key="i">
              <p class="reviews_quote" aria-hidden="true">”</p>
              <h3>{{ review.title }}</h3>
              <p class="reviews_stars" aria-label="Ocena 5 na 5">★★★★★</p>
              <blockquote>{{ review.text }}</blockquote>
              <p class="reviews_author">
                <span>{{ review.author }}</span>
                <span class="reviews_car">{{ review.car }}</span>
              </p>
            </li>
          </ul>

          <button class="reviews_arrow" type="button" aria-label="Następne opinie" @click="scrollReviews(1)">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </section>

    <!-- --------------------------------------------------------- faq -- -->
    <section class="site_section site_section--paper">
      <div class="site_container faq_grid">
        <div>
          <p class="site_eyebrow">FAQ</p>
          <h2>Najczęściej zadawane pytania</h2>
          <p>
            Nie znalazłeś odpowiedzi na swoje pytanie?<br />
            Zadzwoń: <a href="tel:+48509230500">Tel. 509 230 500</a> lub
            <a href="tel:+48501069442">501 069 442</a>
          </p>
        </div>

        <SiteAccordion :items="faq" />
      </div>
    </section>

    <section class="site_container enquiry_wrap">
      <SiteEnquiry />
    </section>
  </div>
</template>

<script>
import pageHead from '@/mixins/pageHead'
import { pageSchema } from '@/utils/schema'
import cars from '@/data/cars'
import SiteMarquee from '@/components/SiteMarquee.vue'
import SiteCounter from '@/components/SiteCounter.vue'
import SiteAccordion from '@/components/SiteAccordion.vue'
import SiteEnquiry from '@/components/SiteEnquiry.vue'
import CarCard from '@/components/CarCard.vue'
import CheckIcon from '@/components/icons/CheckIcon.vue'
import PhoneIcon from '@/components/icons/PhoneIcon.vue'
import ArrowIcon from '@/components/icons/ArrowIcon.vue'

export default {
  name: 'HomePage',
  components: { SiteMarquee, SiteCounter, SiteAccordion, SiteEnquiry, CarCard, CheckIcon, PhoneIcon, ArrowIcon },
  mixins: [pageHead],

  pageHead: {
    title: 'Strona główna - Japonia Cars',
    description:
      'Import samochodów z Japonii bezpośrednio z domów aukcyjnych — pełna obsługa, własny warsztat, jeden numer telefonu.',
    schema: pageSchema({
      path: '/',
      name: 'Strona główna',
      image: '/media/home-serwis.webp',
      published: '2026-05-05T09:32:34+00:00',
      modified: '2026-06-08T11:58:13+00:00',
    }),
  },

  data() {
    return {
      heroImage: '/media/home-hero.webp',
      reason_open: 0,
      brands: ['MAZDA', 'SUBARU', 'LEXUS', 'MITSUBISHI', 'SUZUKI', 'NISSAN', 'TOYOTA', 'HONDA'],
      services: [
        'Transport morski',
        'Odprawy celne',
        'Własny warsztat',
        'Ubezpieczenia OC/AC',
        'Rejestracja w Polsce',
        'Tuning i modyfikacje',
        'Likwidacja szkód',
      ],
      reasons: [
        {
          tab: 'Serwis',
          title: 'Centrum serwisowo-logistyczne pod jednym dachem',
          image: '/media/home-serwis.webp',
          text:
            'Większość importerów kończy swoje działania w momencie, gdy auto trafi do portu. ' +
            'My działamy na każdym etapie procesu. Japonia Cars to jedyny importer samochodów ' +
            'z Japonii posiadający własny warsztat, w którym po przyjeździe pojazdu możesz zlecić ' +
            'naprawę, przegląd techniczny, tuning lub modyfikacje. W tym samym miejscu obsługujemy ' +
            'ubezpieczenia OC i AC, likwidujemy szkody, a do tego zajmujemy się rejestracją pojazdu. ' +
            'Specjalizujemy się również w serwisie sportowych i drogich samochodów, bez przekazywania ' +
            'ich zewnętrznym firmom.',
        },
        {
          tab: 'Wycena',
          title: 'Precyzyjna wycena',
          image: '/media/home-wycena.webp',
          text:
            'Klienci najczęściej zadają sobie pytanie, ile to wszystko będzie kosztować? Koszt wynika ' +
            'z sumy ceny aukcyjnej, kosztów transportu morskiego, cła, akcyzy i odprawy w Polsce, ' +
            'a każda z tych pozycji zależy od konkretnego modelu, jego wartości i pojemności silnika. ' +
            'Potrafimy wyliczyć to z wyprzedzeniem, dokładnie i bez ukrytych pozycji w końcowym ' +
            'rozliczeniu. To właśnie ta zdolność do precyzyjnego przewidywania kosztów wyróżnia nas ' +
            'na tle rynku.',
        },
        {
          tab: 'Doświadczenie',
          title: '10 lat doświadczenia i ponad 5 000 sprowadzonych aut',
          image: '/media/home-doswiadczenie.webp',
          text:
            'Zaczynaliśmy od importu z USA i Emiratów, co dało nam solidne fundamenty logistyczne, ' +
            'zanim skupiliśmy się na Japonii. Dziś to doświadczenie przekłada się na sprawną ' +
            'organizację transportu morskiego, odpraw celnych i formalności, które dla wielu ' +
            'kupujących stanowią nieprzekraczalną barierę. U nas cały proces odbywa się w jednym ' +
            'miejscu i pod jednym numerem telefonu.',
        },
      ],
      paths: [
        {
          eyebrow: '01 — Import na zamówienie',
          theme: 'paths_wide',
          image: '/media/home-import.webp',
          title: 'Powiedz nam, jakie auto Cię interesuje',
          text:
            'Markę, model, rocznik, przebieg, kolor, skrzynię biegów, a my znajdziemy je na japońskich ' +
            'aukcjach. Codziennie aktualizowane oferty dają dostęp do tysięcy pojazdów, których nie ' +
            'znajdziesz na żadnym polskim portalu ogłoszeniowym. Prowadzimy cały proces: od udziału ' +
            'w aukcji, przez transport morski i odprawę celną, aż po dostawę pod Twój adres w Polsce. ' +
            'Przed licytacją oraz po jej wygraniu zlecamy niezależną inspekcję techniczną pojazdu. ' +
            'Jeśli po wygranej aukcji ujawnią się uszkodzenia, o których wcześniej nie było mowy, ' +
            'możesz zrezygnować z odbioru bez konsekwencji.',
        },
        {
          eyebrow: '02 — Import z aukcji',
          theme: 'paths_accent',
          title: 'Wskaż konkretny pojazd z japońskiej aukcji.',
          text:
            'Na naszej stronie możesz przeglądać aktualne oferty z japońskich domów aukcyjnych ' +
            'i wskazać konkretny pojazd, o który chcesz się ubiegać. Aukcje odświeżane są każdego dnia, ' +
            'więc przekrój dostępnych modeli jest szeroki: od codziennych aut, przez youngtimery, ' +
            'po rzadkie egzemplarze niedostępne na europejskim rynku. Zapytanie o dowolne auto dostępne ' +
            'na stronie możesz wysłać bezpośrednio do nas.',
          link: { to: '/aukcje/', label: 'Przeglądaj aukcje' },
        },
        {
          eyebrow: '03 — Pojazdy dostępne w salonie',
          theme: 'paths_light',
          title: 'Auta od ręki — nie czekaj 3 miesięcy.',
          text:
            'Nie chcesz czekać trzech miesięcy? W naszym salonie znajdziesz pojazdy już sprowadzone ' +
            'i gotowe do odbioru lub rejestracji. Każde auto przeszło inspekcję techniczną i zostało ' +
            'przygotowane do sprzedaży z pełną dokumentacją. Możemy również zorganizować ubezpieczenie, ' +
            'rejestrację i transport pod wskazany adres.',
          link: { to: '/samochody/', label: 'Zobacz auta od ręki' },
        },
      ],
      reviews: Array.from({ length: 4 }, () => ({
        title: 'Od początku współpracy miałem poczucie, że wszystko jest prowadzone profesjonalnie.',
        text:
          'Zespół rzeczowo odpowiadał na pytania i wyjaśniał kolejne etapy. Samochód po sprowadzeniu ' +
          'odpowiada opisowi i moim oczekiwaniom.',
        author: 'Tomasz K.',
        car: 'TOYOTA SUPRA A80 · 2024',
      })),
      faq: [
        {
          question: 'Ile wyniesie cena sprowadzonego auta?',
          answer:
            'To zależy od ceny aukcyjnej, kursu jena, kosztów transportu morskiego, cła, akcyzy ' +
            'i odprawy celnej w Polsce. Każda z tych pozycji może podlegać zmianie i zależeć od ' +
            'konkretnego modelu oraz pojemności silnika. Przygotowujemy szczegółowe wyliczenie przed ' +
            'licytacją, bez ogólników i ukrytych pozycji.',
        },
        {
          question: 'Czy zajmujecie się odbiorem z portu, dokumentami i rejestracją?',
          answer:
            'Tak. Odbieramy auto z portu, przeprowadzamy odprawę celną, tłumaczymy japońską ' +
            'dokumentację i zajmujemy się pełną rejestracją pojazdu w Polsce.',
        },
        {
          question: 'Czy możecie naprawić, serwisować lub zmodyfikować sprowadzone auto?',
          answer:
            'Tak. Posiadamy własny warsztat, w którym wykonujemy naprawy, przeglądy, serwis, tuning ' +
            'i modyfikacje, w tym obsługę sportowych i droższych samochodów. Zajmujemy się też ' +
            'ubezpieczeniem OC i AC oraz dostawą pod wskazany adres.',
        },
        {
          question: 'Czy mogę sprawdzić stan auta przed zakupem?',
          answer:
            'Tak. Przed licytacją i po jej wygraniu zlecamy niezależną inspekcję techniczną. ' +
            'Jeśli ujawni ona uszkodzenia, możesz zrezygnować z odbioru.',
        },
      ],
    }
  },

  computed: {
    // The four newest cars; the rest live on /samochody/.
    featuredCars() {
      return cars.slice(0, 4)
    },
  },

  methods: {
    scrollReviews(direction) {
      const rail = this.$refs.reviews
      rail.scrollBy({ left: direction * rail.clientWidth * 0.5, behavior: 'smooth' })
    },
  },
}
</script>

<style scoped>
/* ------------------------------------------------------------- hero --- */

.hero {
  position: relative;
  display: flex;
  align-items: center;
  min-height: 855px;
  padding: 74px 0 94px;
  background: var(--ink) center / cover no-repeat;
  background-attachment: fixed;
  color: var(--ink-invert-soft);
  text-align: center;
}

.hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(rgba(0, 0, 0, 0.2) 0%, rgb(0, 0, 0) 98%);
}

.hero > * {
  position: relative;
}

.hero .site_eyebrow {
  justify-content: center;
  color: var(--accent);
}

.hero h1 {
  color: var(--ink-invert);
}

.hero_subtitle {
  margin-bottom: 20px;
  color: rgba(247, 250, 252, 0.9);
  font-family: 'Space Grotesk', sans-serif;
  font-size: 35px;
  letter-spacing: -0.035em;
}

.hero_actions {
  margin-bottom: 32px;
}

.hero_lead {
  max-width: 683px;
  margin: -10px auto 15px;
}

/* ----------------------------------------------------------- why us --- */

.why_header {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: end;
  margin-bottom: 48px;
}

.why_header h2 {
  margin-top: 15px;
}

.why_tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 32px;
}

.why_tab {
  padding: 10.5px 25px;
  border: 1px solid var(--paper-line);
  border-radius: 100px;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.why_tab.is_active {
  border-color: var(--ink);
  background: var(--ink);
  color: var(--ink-invert);
}

.why_panel {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
}

.why_card {
  padding: 32px;
  border-radius: var(--radius);
  background: var(--paper);
}

.why_panel h3 {
  margin: 15px 0 18.5px;
}

.why_panel img {
  width: 100%;
  max-height: 520px;
  border-radius: var(--radius);
  object-fit: cover;
}

/* ------------------------------------------------------------ paths --- */

.paths {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
}

.paths li {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 32px;
  border-radius: var(--radius);
  background: #fff;
}

.paths li > div {
  display: flex;
  flex-direction: column;
  gap: 16px;
  height: 100%;
}

.paths .site_button {
  align-self: flex-start;
  margin-top: auto;
}

.paths h3 {
  font-size: 26px;
}

/* 01 — full width, black, with the photo alongside the copy */
.paths li.paths_wide {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: 804fr 418fr;
  gap: 32px;
  padding: 0 0 0 32px;
  background: var(--ink);
  color: var(--ink-invert-soft);
  overflow: hidden;
}

.paths li.paths_wide > div {
  padding: 32px 0;
}

.paths_wide h3 {
  color: var(--ink-invert);
}

.paths_wide figure,
.paths_wide img {
  height: 100%;
}

.paths_wide img {
  width: 100%;
  object-fit: cover;
}

/* 02 — the accent card */
.paths li.paths_accent {
  background: var(--accent);
  color: var(--ink-invert);
}

.paths_accent h3 {
  color: var(--ink-invert);
}

.paths_accent .site_eyebrow {
  color: var(--ink-invert);
}

.paths_accent .site_button {
  background: var(--ink);
}

/* ------------------------------------------------------------ about --- */

.about_grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: center;
}

.about_grid h2 {
  margin: 15px 0 22.5px;
}

.about_lead {
  margin-bottom: 32px;
}

.about_score {
  padding: 32px;
  border-radius: var(--radius);
  background: var(--ink);
  color: var(--ink-invert-soft);
}

.about_score :deep(.counter) {
  color: var(--ink-invert);
}

.about_score img {
  width: 100%;
  margin-top: 24px;
  border-radius: var(--radius);
  object-fit: cover;
}

.stock_grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
}

.brands {
  padding: 36px 0;
  background: var(--paper);
  color: var(--ink-muted);
  font-weight: 700;
}

.brands :deep(.marquee) {
  --marquee-gap: 84px;
}

/* ---------------------------------------------------------- reviews --- */

.reviews {
  display: flex;
  align-items: center;
  gap: 16px;
}

.reviews_track {
  display: flex;
  flex: 1;
  gap: 30px;
  padding-top: 35px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
}

.reviews_track::-webkit-scrollbar {
  display: none;
}

.reviews li {
  position: relative;
  flex: 0 0 calc(50% - 15px);
  padding: 20px;
  border: 1px solid var(--paper-line);
  border-radius: 10px;
  scroll-snap-align: start;
}

.reviews_quote {
  position: absolute;
  top: -55px;
  left: 20px;
  color: var(--paper-line);
  font-size: 90px;
  line-height: 1;
}

.reviews_arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 48px;
  flex: none;
  padding: 0;
  border: 0;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  cursor: pointer;
}

.reviews_arrow svg {
  width: 18px;
  height: 18px;
}

.reviews h3 {
  margin-bottom: 10px;
  font-size: 20px;
  line-height: 1.4;
}

.reviews_stars {
  margin-bottom: 10px;
  color: var(--accent);
  letter-spacing: 2px;
}

.reviews blockquote {
  margin: 0;
}

.reviews_author {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 20px;
  color: rgba(10, 10, 11, 0.88);
  font-size: 20px;
}

.reviews_car {
  font-size: 15px;
  color: var(--ink-body);
}

/* -------------------------------------------------------------- faq --- */

.faq_grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 64px;
  align-items: start;
}

.faq_grid h2 {
  margin: 15px 0 32px;
}

.enquiry_wrap {
  padding: 80px 0;
}

@media (max-width: 1024px) {
  .why_header,
  .why_panel,
  .about_grid,
  .faq_grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .paths,
  .stock_grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .paths_wide {
    grid-template-columns: 1fr;
    padding: 0;
  }

  .paths_wide > div {
    padding: 32px;
  }
}

@media (max-width: 767px) {
  .hero {
    min-height: 600px;
    background-attachment: scroll;
  }

  .hero_subtitle {
    font-size: 26px;
  }

  .paths,
  .stock_grid {
    grid-template-columns: 1fr;
  }

  .reviews li {
    flex-basis: 100%;
  }
}
</style>
